import { useState, useEffect, useCallback, Fragment } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const KNOWLEDGE_CATEGORIES = [
  "brochures", "drinks_packages", "restaurant_menus", "cabins", "ships",
  "excursions", "wifi", "tariffs", "terms", "cancellation", "loyalty",
  "accessibility", "destinations", "promotions", "faqs",
];
const VISIBILITY_LEVELS = ["PUBLIC", "MEMBER", "PARTNER_RESTRICTED", "ADMIN", "SUPER_ADMIN"];
// Phase finale — modèle MULTI-AUDIENCE (plusieurs valeurs simultanées). L'AUDIENCE
// est un concept distinct de l'APPROBATION. Libellés FR imposés par le brief.
const AUDIENCES = [
  { value: "PUBLIC", label: "Public" },
  { value: "MEMBER", label: "Adhérents" },
  { value: "PARTNER_RESTRICTED", label: "Partenaires" },
  { value: "ADMIN", label: "Administrateurs" },
  { value: "SUPER_ADMIN", label: "Super administrateurs" },
];
const AUD_FR = AUDIENCES.reduce((m, a) => { m[a.value] = a.label; return m; }, {});
const audFr = (v) => AUD_FR[v] || v;
// Back-office 100% FR (Phase 16) — libellés lisibles pour valeurs enum internes (EN conservées en DB).
const CATEGORY_FR = {
  brochures: "Brochures", drinks_packages: "Forfaits boissons", restaurant_menus: "Menus restaurants",
  cabins: "Cabines", ships: "Navires", excursions: "Excursions", wifi: "Wi-Fi", tariffs: "Tarifs",
  terms: "Conditions", cancellation: "Annulation", loyalty: "Fidélité", accessibility: "Accessibilité",
  destinations: "Destinations", promotions: "Promotions", faqs: "FAQ",
};
const STATUS_FR = {
  draft: "Brouillon", approved: "Approuvé", active: "Actif", archived: "Archivé",
  rejected: "Rejeté", superseded: "Remplacé",
};
const VALIDITY_FR = {
  CURRENT: "En cours", EXPIRING: "Expire bientôt", EXPIRED: "Expiré", SUPERSEDED: "Remplacé",
  REVOKED: "Révoqué", UNTIL_REPLACED: "Jusqu'à remplacement", NO_EXPIRATION: "Sans expiration",
};
const VISIBILITY_FR = {
  PUBLIC: "Public", MEMBER: "Membre", PARTNER_RESTRICTED: "Partenaire (restreint)",
  ADMIN: "Admin", SUPER_ADMIN: "Super admin",
};
const LABEL_FR = {
  factual: "Fait vérifié", promotional_claim: "Contenu promotionnel",
  partner_claim: "Déclaration partenaire", editorial_observation: "Observation ITERCATUS",
};
const catFr = (c) => CATEGORY_FR[c] || c;
const statusFr = (s) => STATUS_FR[s] || s;
const validityFr = (v) => VALIDITY_FR[v] || v;
const visFr = (v) => VISIBILITY_FR[v] || v;
const labelFr = (l) => LABEL_FR[l] || l;
const LORE_KINDS = ["nickname", "habit", "personality_fact", "expression", "anecdote", "family_memory", "easter_egg"];
const LORE_VIS = ["PUBLIC", "AUTHENTICATED", "FAMILY", "SPECIFIC_USERS", "ADMIN_ONLY"];

function useAuthedFetch() {
  const { token } = useAuth();
  return useCallback(async (path, opts = {}) => {
    const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (opts.body instanceof FormData) delete headers["Content-Type"];
    const r = await fetch(`${API}${path}`, { ...opts, headers });
    const text = await r.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
    if (!r.ok) throw new Error((data && (data.detail || data.error)) || `HTTP ${r.status}`);
    return data;
  }, [token]);
}

function PartnersTab({ fetcher }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", slug: "", official_website: "", status: "prospective" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await fetcher("/admin/blue/partners"); setPartners(d.partners || []); }
    catch (e) { toast.error(`Chargement partenaires: ${e.message}`); }
    setLoading(false);
  }, [fetcher]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await fetcher("/admin/blue/partners", { method: "POST", body: JSON.stringify(form) });
      toast.success("Partenaire enregistré");
      setForm({ name: "", slug: "", official_website: "", status: "prospective" });
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="blue-partners-tab">
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end p-4 border border-border rounded-md bg-card">
        <div><label className="text-xs text-muted-foreground">Nom</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="partner-name-input" /></div>
        <div><label className="text-xs text-muted-foreground">Slug (optionnel)</label>
          <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="partner-slug-input" /></div>
        <div><label className="text-xs text-muted-foreground">Site officiel</label>
          <Input value={form.official_website} onChange={(e) => setForm({ ...form, official_website: e.target.value })} data-testid="partner-site-input" /></div>
        <Button type="submit" data-testid="partner-save-btn">Enregistrer</Button>
      </form>
      {loading ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary"><tr><th className="text-left p-2">Partenaire</th><th className="text-left p-2">Slug</th><th className="text-left p-2">Statut</th><th className="text-left p-2">Site</th></tr></thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.id} className="border-t border-border" data-testid={`partner-row-${p.slug}`}>
                  <td className="p-2 font-medium">{p.name}</td><td className="p-2">{p.slug}</td>
                  <td className="p-2">{p.status}</td><td className="p-2">{p.official_website || "—"}</td>
                </tr>
              ))}
              {partners.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Aucun partenaire enregistré.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DocumentsTab({ fetcher }) {
  const [docs, setDocs] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ partner_id: "", title: "", category: "brochures", visibility: "MEMBER", audiences: [], language: "fr", validity_mode: "FIXED_DATE", expiry_date: "", source_url: "", label: "factual", embargo_until: "", source_version: "", source_date: "" });
  const [files, setFiles] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [uploading, setUploading] = useState(false);
  const [batch, setBatch] = useState(null);
  // Phase finale — sélection multi-documents + action « Modifier les publics ».
  const [selected, setSelected] = useState({});
  const [bulkAudiences, setBulkAudiences] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  // Phase corrective — pagination serveur + filtres + diffusion fichier.
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState({ search: "", partner: "", category: "", status: "", audience: "", validity: "", entity_type: "", document_type: "" });
  const [bulkFd, setBulkFd] = useState("");
  const [menuOpen, setMenuOpen] = useState(null);

  const toggleFormAudience = (v) => setForm((f) => ({ ...f, audiences: f.audiences.includes(v) ? f.audiences.filter((x) => x !== v) : [...f.audiences, v] }));
  const toggleBulkAudience = (v) => setBulkAudiences((a) => a.includes(v) ? a.filter((x) => x !== v) : [...a, v]);
  const toggleSelected = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };

  const saveBulkAudiences = async () => {
    if (!selectedIds.length) { toast.error("Aucun document sélectionné"); return; }
    setSavingBulk(true);
    try {
      const r = await fetcher("/admin/blue/documents/audiences", { method: "POST", body: JSON.stringify({ document_ids: selectedIds, audiences: bulkAudiences }) });
      if (r.ok === false) throw new Error(r.detail || r.error || "Échec");
      toast.success(`Publics mis à jour : ${r.modified} document(s) — sans réingestion`);
      setShowBulk(false); setSelected({}); setBulkAudiences([]); load();
    } catch (e) { toast.error(e.message); }
    setSavingBulk(false);
  };

  const saveBulkFd = async () => {
    if (!selectedIds.length) { toast.error("Aucun document sélectionné"); return; }
    if (!bulkFd) { toast.error("Choisissez une diffusion"); return; }
    try {
      const r = await fetcher("/admin/blue/documents/file-distribution", { method: "POST", body: JSON.stringify({ document_ids: selectedIds, file_distribution: bulkFd }) });
      if (r.ok === false) throw new Error(r.detail || r.error || "Échec");
      toast.success(`Diffusion mise à jour : ${r.modified} document(s)`);
      setSelected({}); setBulkFd(""); load();
    } catch (e) { toast.error(e.message); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      Object.entries(filters).forEach(([k, v]) => { if (v) qs.append(k, v); });
      const [d, p] = await Promise.all([
        fetcher(`/admin/blue/documents?${qs.toString()}`),
        fetcher("/admin/blue/partners"),
      ]);
      setDocs(d.items || d.documents || []); setTotal(d.total ?? (d.documents || []).length);
      setPartners(p.partners || []);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [fetcher, page, pageSize, filters]);

  useEffect(() => { load(); }, [load]);

  const pollBatch = useCallback(async (batchId) => {
    try {
      const b = await fetcher(`/admin/blue/documents/batch/${batchId}`);
      setBatch(b);
      if (b.status === "done") {
        setUploading(false);
        const c = b.counts || {};
        toast.success(`Lot terminé : ${c.succeeded || 0} succès · ${c.duplicates || 0} doublons · ${c.failed || 0} échecs (${b.total})`);
        load();
      } else {
        setTimeout(() => pollBatch(batchId), 1500);
      }
    } catch (e) { setUploading(false); toast.error(e.message); }
  }, [fetcher, load]);

  const upload = async (e) => {
    e.preventDefault();
    if (uploading) return; // anti double-soumission
    if (!files.length) { toast.error("Fichier(s) requis"); return; }
    if (!form.partner_id) { toast.error("Partenaire requis"); return; }
    if (form.validity_mode === "FIXED_DATE" && !form.expiry_date) { toast.error("Expiration requise pour « Jusqu'à une date »"); return; }
    setUploading(true);
    try {
      if (files.length === 1) {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => { if (k === "audiences") return; if (v) fd.append(k, v); });
        if (form.audiences.length) fd.append("audiences", form.audiences.join(","));
        fd.append("file", files[0]);
        const r = await fetcher("/admin/blue/documents", { method: "POST", body: fd });
        if (r.ok === false) { toast.error(r.error || "Erreur"); setUploading(false); return; }
        if (r.dedup) toast.info("Document déjà connu (idempotence sha256) — aucune re-ingestion");
        else toast.success("Document ingéré (statut draft)");
        if (r.warnings && r.warnings.length) toast.warning(`Avertissements: ${r.warnings.join(", ")}`);
        setFiles([]); setUploading(false); load();
      } else {
        // Lot > 1 fichier → ASYNCHRONE : réponse immédiate + suivi de progression (zéro 502).
        const fd = new FormData();
        fd.append("partner_id", form.partner_id);
        const { partner_id, title, ...shared } = form; // eslint-disable-line no-unused-vars
        fd.append("shared_metadata", JSON.stringify(shared));
        files.forEach((f) => fd.append("files", f));
        const r = await fetcher("/admin/blue/documents/batch-async", { method: "POST", body: fd });
        if (!r.batch_id) { setUploading(false); toast.error("Échec du lancement du lot"); return; }
        setFiles([]);
        setBatch({ id: r.batch_id, total: r.total, status: "processing", counts: { processed: 0 } });
        toast.info(`Lot lancé (${r.total} fichiers) — traitement en arrière-plan`);
        pollBatch(r.batch_id);
      }
    } catch (e) { setUploading(false); toast.error(e.message); }
  };

  const replaceDoc = async (d) => {
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = async () => {
      const f = input.files?.[0]; if (!f) return;
      const sv = window.prompt("Réf./version de la nouvelle version (ex. V2, 03/2027) :", "") || "";
      const fd = new FormData();
      fd.append("partner_id", d.partner_id);
      if (d.title) fd.append("title", d.title);
      if (sv) fd.append("source_version", sv);
      fd.append("validity_mode", d.validity_mode || "UNTIL_REPLACED");
      fd.append("file", f);
      try {
        const r = await fetcher(`/admin/blue/documents/${d.id}/replace`, { method: "POST", body: fd });
        if (r.ok === false) throw new Error(r.error || "Échec");
        if (r.dedup) toast.info("Fichier identique déjà présent — ancienne version conservée");
        else toast.success("Nouvelle version ingérée (draft). Approuvez-la pour qu'elle remplace l'actuelle.");
        load();
      } catch (e) { toast.error(e.message); }
    };
    input.click();
  };

  const approve = async (id) => {
    try { await fetcher(`/admin/blue/documents/${id}/approve`, { method: "POST" }); toast.success("Document approuvé"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Motif du rejet (optionnel) :", "") || "";
    if (reason === null) return;
    try {
      const r = await fetcher(`/admin/blue/documents/${id}/reject`, { method: "POST", body: JSON.stringify({ reason }) });
      if (r.ok === false) throw new Error(r.error || "Échec");
      toast.success("Document rejeté"); load();
    } catch (e) { toast.error(e.message); }
  };

  const supersede = async (id) => {
    const newId = window.prompt("ID du document qui remplace celui-ci :", "");
    if (!newId) return;
    try {
      const r = await fetcher(`/admin/blue/documents/${id}/supersede`, { method: "POST", body: JSON.stringify({ new_document_id: newId.trim() }) });
      if (r.ok === false) throw new Error(r.error || "Échec");
      toast.success("Document remplacé (superseded)"); load();
    } catch (e) { toast.error(e.message); }
  };

  const flagReview = async (id, defaultFlag) => {
    const flag = window.prompt("Signalement (DATE_REVIEW_REQUIRED, KNOWLEDGE_CONFLICT, MANUAL_REVIEW) :", defaultFlag || "MANUAL_REVIEW");
    if (!flag) return;
    const note = window.prompt("Note reviewer (optionnel) :", "") || "";
    try {
      const r = await fetcher(`/admin/blue/documents/${id}/review-flag`, { method: "POST", body: JSON.stringify({ flag: flag.trim(), note }) });
      if (r.ok === false) throw new Error(r.error || "Échec");
      toast.success(`Signalement ${flag} posé`); load();
    } catch (e) { toast.error(e.message); }
  };

  const proposePromo = async (id, orgHint) => {
    const orgId = window.prompt("ID de l'organisation partenaire (org_id) :", orgHint || "");
    if (!orgId) return;
    const title = window.prompt("Titre de la promotion à proposer :", "") || "";
    try {
      const r = await fetcher(`/admin/blue/documents/${id}/propose-promotion`, { method: "POST", body: JSON.stringify({ org_id: orgId.trim(), title }) });
      if (r.ok === false) throw new Error(r.error || "Échec");
      toast.success(`Offre draft créée : ${r.offer_id?.slice(0, 8) || "OK"}`); load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleDetails = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="space-y-6" data-testid="blue-docs-tab">
      <form onSubmit={upload} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end p-4 border border-border rounded-md bg-card">
        <div><label className="text-xs text-muted-foreground">Partenaire</label>
          <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
            <SelectTrigger data-testid="doc-partner-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
            <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><label className="text-xs text-muted-foreground">Titre</label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title-input" /></div>
        <div><label className="text-xs text-muted-foreground">Catégorie</label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{KNOWLEDGE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{catFr(c)}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><label className="text-xs text-muted-foreground">Visibilité (héritée / repli)</label>
          <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
            <SelectTrigger data-testid="doc-visibility-select"><SelectValue /></SelectTrigger>
            <SelectContent>{VISIBILITY_LEVELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="md:col-span-3" data-testid="doc-audiences-group">
          <label className="text-xs text-muted-foreground">Publics autorisés (plusieurs possibles)</label>
          <div className="flex flex-wrap gap-4 mt-1">
            {AUDIENCES.map((a) => (
              <label key={a.value} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`doc-audience-${a.value}`}>
                <input type="checkbox" checked={form.audiences.includes(a.value)} onChange={() => toggleFormAudience(a.value)} className="h-4 w-4" />
                {a.label}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">Sans coche, la visibilité héritée ci-dessus sert d'audience initiale. L'audience ne remplace pas l'approbation.</p>
        </div>
        <div><label className="text-xs text-muted-foreground">Label (Phase 10)</label>
          <Select value={form.label} onValueChange={(v) => setForm({ ...form, label: v })}>
            <SelectTrigger data-testid="doc-label-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="factual">factual — fait vérifié</SelectItem>
              <SelectItem value="promotional_claim">promotional_claim — claim partenaire</SelectItem>
              <SelectItem value="partner_claim">partner_claim — info partenaire</SelectItem>
              <SelectItem value="editorial_observation">editorial_observation</SelectItem>
            </SelectContent>
          </Select></div>
        <div><label className="text-xs text-muted-foreground">Embargo jusqu'à (ISO)</label>
          <Input value={form.embargo_until} onChange={(e) => setForm({ ...form, embargo_until: e.target.value })} placeholder="2026-12-01T00:00:00Z" data-testid="doc-embargo-input" /></div>
        <div><label className="text-xs text-muted-foreground">Validité</label>
          <Select value={form.validity_mode} onValueChange={(v) => setForm({ ...form, validity_mode: v })}>
            <SelectTrigger data-testid="doc-validity-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="FIXED_DATE">Jusqu'à une date</SelectItem>
              <SelectItem value="UNTIL_REPLACED">Jusqu'à remplacement</SelectItem>
              <SelectItem value="NO_EXPIRATION">Sans date d'expiration</SelectItem>
            </SelectContent>
          </Select></div>
        {form.validity_mode === "FIXED_DATE" && (
          <div><label className="text-xs text-muted-foreground">Expiration (ISO) — requis</label>
            <Input value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} placeholder="2027-01-01" data-testid="doc-expiry-input" /></div>
        )}
        <div><label className="text-xs text-muted-foreground">Réf./version source</label>
          <Input value={form.source_version} onChange={(e) => setForm({ ...form, source_version: e.target.value })} placeholder="V2, Edition 2026, Rev. 4" data-testid="doc-source-version-input" /></div>
        <div><label className="text-xs text-muted-foreground">Date/version source</label>
          <Input value={form.source_date} onChange={(e) => setForm({ ...form, source_date: e.target.value })} placeholder="2026-02" data-testid="doc-source-date-input" /></div>
        <div><label className="text-xs text-muted-foreground">Fichier(s)</label>
          <Input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="doc-file-input" />
          {files.length > 1 && <span className="text-xs text-accent">{files.length} fichiers — ingestion par lot</span>}</div>
        <Button type="submit" disabled={uploading} data-testid="doc-upload-btn">{uploading ? "Ingestion en cours…" : (files.length > 1 ? `Ingérer le lot (${files.length})` : "Ingérer")}</Button>
      </form>
      {batch && (
        <div className="mb-4 border border-accent/30 bg-accent/5 rounded-md p-3 text-sm" data-testid="batch-progress">
          <div className="font-medium">{batch.status === "done" ? "Lot terminé" : "Traitement du lot en cours…"}</div>
          <div className="text-muted-foreground" data-testid="batch-progress-text">
            {(batch.counts?.processed ?? 0)} / {batch.total} traités · Réussis : {batch.counts?.succeeded ?? 0} · Doublons : {batch.counts?.duplicates ?? 0} · Échecs : {batch.counts?.failed ?? 0}
          </div>
          <div className="mt-2 h-1.5 bg-secondary rounded overflow-hidden">
            <div className="h-full bg-accent transition-all" style={{ width: `${Math.round(100 * (batch.counts?.processed || 0) / (batch.total || 1))}%` }} />
          </div>
        </div>
      )}
      {loading ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <>
        <div className="flex flex-wrap items-end gap-2 mb-3 p-3 border border-border rounded-md bg-card" data-testid="docs-filters">
          <div><label className="text-[11px] text-muted-foreground block">Recherche</label>
            <Input value={filters.search} onChange={(e) => setFilter("search", e.target.value)} placeholder="Titre, navire, fichier…" className="h-8 w-52" data-testid="filter-search" /></div>
          <div><label className="text-[11px] text-muted-foreground block">Partenaire</label>
            <select value={filters.partner} onChange={(e) => setFilter("partner", e.target.value)} className="h-8 border border-border rounded px-2 text-sm bg-background" data-testid="filter-partner">
              <option value="">Tous</option>{partners.map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
            </select></div>
          <div><label className="text-[11px] text-muted-foreground block">Catégorie</label>
            <select value={filters.category} onChange={(e) => setFilter("category", e.target.value)} className="h-8 border border-border rounded px-2 text-sm bg-background" data-testid="filter-category">
              <option value="">Toutes</option>{KNOWLEDGE_CATEGORIES.map((c) => <option key={c} value={c}>{catFr(c)}</option>)}
            </select></div>
          <div><label className="text-[11px] text-muted-foreground block">Statut</label>
            <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)} className="h-8 border border-border rounded px-2 text-sm bg-background" data-testid="filter-status">
              <option value="">Tous</option>{Object.entries(STATUS_FR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className="text-[11px] text-muted-foreground block">Public</label>
            <select value={filters.audience} onChange={(e) => setFilter("audience", e.target.value)} className="h-8 border border-border rounded px-2 text-sm bg-background" data-testid="filter-audience">
              <option value="">Tous</option>{AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select></div>
          <div><label className="text-[11px] text-muted-foreground block">Validité</label>
            <select value={filters.validity} onChange={(e) => setFilter("validity", e.target.value)} className="h-8 border border-border rounded px-2 text-sm bg-background" data-testid="filter-validity">
              <option value="">Toutes</option>{Object.entries(VALIDITY_FR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className="text-[11px] text-muted-foreground block">Type d'entité</label>
            <select value={filters.entity_type} onChange={(e) => setFilter("entity_type", e.target.value)} className="h-8 border border-border rounded px-2 text-sm bg-background" data-testid="filter-entity-type">
              <option value="">Tous</option><option value="SHIP">Navire</option>
            </select></div>
          <div><label className="text-[11px] text-muted-foreground block">Type de doc.</label>
            <select value={filters.document_type} onChange={(e) => setFilter("document_type", e.target.value)} className="h-8 border border-border rounded px-2 text-sm bg-background" data-testid="filter-document-type">
              <option value="">Tous</option><option value="TECHNICAL_SHEET">Fiche technique</option><option value="BROCHURE">Brochure</option><option value="DOCUMENT">Document</option>
            </select></div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-2" data-testid="bulk-audiences-bar">
          <span className="text-xs text-muted-foreground">{selectedIds.length} sélectionné(s)</span>
          <Button size="sm" variant="outline" disabled={!selectedIds.length} onClick={() => setShowBulk((s) => !s)} data-testid="bulk-audiences-toggle">Modifier les publics</Button>
          <select value={bulkFd} onChange={(e) => setBulkFd(e.target.value)} disabled={!selectedIds.length} className="h-8 border border-border rounded px-2 text-sm bg-background" data-testid="bulk-fd-select">
            <option value="">Diffusion…</option><option value="INTERNAL">Interne</option><option value="ANSWER_ONLY">Réponse uniquement</option><option value="PUBLIC_DOWNLOAD">Document public téléchargeable</option>
          </select>
          <Button size="sm" variant="outline" disabled={!selectedIds.length || !bulkFd} onClick={saveBulkFd} data-testid="bulk-fd-save">Appliquer la diffusion</Button>
          {selectedIds.length > 0 && <Button size="sm" variant="ghost" onClick={() => setSelected({})} data-testid="bulk-audiences-clear">Tout désélectionner</Button>}
        </div>
        {showBulk && (
          <div className="mb-3 border border-accent/40 bg-accent/5 rounded-md p-3" data-testid="bulk-audiences-panel">
            <div className="text-sm font-medium mb-2">Publics des {selectedIds.length} document(s) sélectionné(s)</div>
            <div className="flex flex-wrap gap-4">
              {AUDIENCES.map((a) => (
                <label key={a.value} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`bulk-audience-${a.value}`}>
                  <input type="checkbox" checked={bulkAudiences.includes(a.value)} onChange={() => toggleBulkAudience(a.value)} className="h-4 w-4" />
                  {a.label}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Enregistrement SANS réingestion : l'ID, le fichier source, l'approbation, la version et la provenance sont préservés.</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" disabled={savingBulk} onClick={saveBulkAudiences} data-testid="bulk-audiences-save">{savingBulk ? "Enregistrement…" : "Enregistrer les publics"}</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowBulk(false)}>Annuler</Button>
            </div>
          </div>
        )}
        <div className="border border-border rounded-md overflow-x-auto" data-testid="documents-table-wrap">
          <table className="w-full text-sm">
            <thead className="bg-secondary"><tr>
              <th className="p-2 w-8"></th>
              <th className="text-left p-2">Document</th>
              <th className="text-left p-2">Partenaire</th>
              <th className="text-left p-2">Catégorie</th>
              <th className="text-left p-2">Publics</th>
              <th className="text-left p-2">Statut · Validité</th>
              <th className="p-2 w-10">Actions</th>
            </tr></thead>
            <tbody>
              {docs.map((d) => {
                const auds = (d.audiences && d.audiences.length) ? d.audiences : (d.visibility ? [d.visibility] : []);
                const audSummary = auds.length ? (auds.length > 1 ? `${audFr(auds[0])} +${auds.length - 1}` : audFr(auds[0])) : "—";
                const audFull = auds.map(audFr).join(", ") + (d.audiences && d.audiences.length ? "" : " (hérité)");
                const fdFr = { INTERNAL: "Interne", ANSWER_ONLY: "Réponse uniquement", PUBLIC_DOWNLOAD: "Public téléchargeable" }[d.file_distribution || "INTERNAL"];
                const displayTitle = d.display_title || d.primary_entity || (d.identification_confidence === "low" ? "À vérifier" : (d.title || d.id.slice(0, 8)));
                return (
                <Fragment key={d.id}>
                  <tr className="border-t border-border" data-testid={`doc-row-${d.id}`}>
                    <td className="p-2 align-top"><input type="checkbox" checked={!!selected[d.id]} onChange={() => toggleSelected(d.id)} className="h-4 w-4" data-testid={`doc-select-${d.id}`} /></td>
                    <td className="p-2 font-medium max-w-[280px]">
                      <button onClick={() => toggleDetails(d.id)} className="underline decoration-dotted block truncate max-w-[270px] text-left" title={displayTitle} data-testid={`doc-details-${d.id}`}>{displayTitle}</button>
                      <span className="text-[11px] text-muted-foreground">{d.document_type === "TECHNICAL_SHEET" ? "Fiche technique" : (d.document_type || "")}{d.entity_type ? ` · ${d.entity_type === "SHIP" ? "Navire" : d.entity_type}` : ""}</span>
                    </td>
                    <td className="p-2 text-xs">{d.partner_slug}</td>
                    <td className="p-2 text-xs">{catFr(d.category)}</td>
                    <td className="p-2 text-xs">
                      <span className="font-medium" title={audFull} data-testid={`doc-audiences-${d.id}`}>{audSummary}</span>
                      <br /><span className="text-[11px] text-muted-foreground" title={`Diffusion fichier : ${fdFr}`} data-testid={`doc-fd-${d.id}`}>{fdFr}</span>
                    </td>
                    <td className="p-2 text-xs">{statusFr(d.status)}<br /><span className="text-muted-foreground">{validityFr(d.validity_status)}</span></td>
                    <td className="p-2 relative text-right whitespace-nowrap">
                      <button className="px-2 py-1 border border-border rounded hover:bg-secondary" onClick={() => setMenuOpen(menuOpen === d.id ? null : d.id)} data-testid={`doc-actions-${d.id}`}>⋯</button>
                      {menuOpen === d.id && (
                        <div className="absolute right-2 z-20 mt-1 bg-card border border-border rounded shadow-md text-left min-w-[170px]" onMouseLeave={() => setMenuOpen(null)}>
                          {d.status === "draft" && <button className="block w-full text-left px-3 py-1.5 hover:bg-secondary" onClick={() => { setMenuOpen(null); approve(d.id); }} data-testid={`doc-approve-${d.id}`}>Approuver</button>}
                          {["draft", "approved", "active"].includes(d.status) && <button className="block w-full text-left px-3 py-1.5 hover:bg-secondary" onClick={() => { setMenuOpen(null); reject(d.id); }} data-testid={`doc-reject-${d.id}`}>Rejeter</button>}
                          {d.status === "active" && <button className="block w-full text-left px-3 py-1.5 hover:bg-secondary" onClick={() => { setMenuOpen(null); supersede(d.id); }} data-testid={`doc-supersede-${d.id}`}>Superséder</button>}
                          {d.status === "active" && d.is_current && <button className="block w-full text-left px-3 py-1.5 hover:bg-secondary" onClick={() => { setMenuOpen(null); replaceDoc(d); }} data-testid={`doc-replace-${d.id}`}>Remplacer</button>}
                          <button className="block w-full text-left px-3 py-1.5 hover:bg-secondary" onClick={() => { setMenuOpen(null); flagReview(d.id); }} data-testid={`doc-flag-${d.id}`}>Signaler</button>
                          {d.category === "promotions" && d.status === "active" && <button className="block w-full text-left px-3 py-1.5 hover:bg-secondary" onClick={() => { setMenuOpen(null); proposePromo(d.id, d.partner_id); }} data-testid={`doc-promo-${d.id}`}>→ Offre</button>}
                          <button className="block w-full text-left px-3 py-1.5 hover:bg-secondary" onClick={() => { setMenuOpen(null); toggleDetails(d.id); }}>Détails</button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {expanded[d.id] && (
                    <tr className="bg-secondary/30">
                      <td colSpan={7} className="p-3 text-xs">
                        <div className="grid md:grid-cols-2 gap-3">
                          <div><strong>Titre affiché :</strong> {displayTitle}</div>
                          <div><strong>Nom de fichier original :</strong> <code>{d.original_filename || "—"}</code></div>
                          <div><strong>Entité principale :</strong> {d.primary_entity || "—"} ({d.entity_type || "—"})</div>
                          <div><strong>Type de document :</strong> {d.document_type || "—"} · <strong>Confiance :</strong> {d.identification_confidence || "—"}</div>
                          <div><strong>Diffusion fichier :</strong> {fdFr}</div>
                          <div><strong>Publics :</strong> {audFull}</div>
                          <div><strong>ID :</strong> <code>{d.id}</code></div>
                          <div><strong>Checksum SHA-256 :</strong> <code>{(d.checksum_sha256 || "").slice(0, 16)}…</code></div>
                          <div><strong>Version :</strong> {d.source_version || `v${d.version || 1}`} · {d.source_date || "—"}</div>
                          <div><strong>Importé :</strong> {d.imported_at} par {d.created_by || "—"}</div>
                          <div><strong>Effectif du :</strong> {d.effective_date || "—"} → <strong>Expire :</strong> {d.expiry_date || "—"}</div>
                          <div><strong>Flags :</strong> {(d.review_flags || []).join(", ") || "—"}{d.embargo_until ? ` · embargo→${d.embargo_until.slice(0, 10)}` : ""}{d.superseded_by ? ` · → ${d.superseded_by.slice(0, 8)}` : ""}</div>
                          {(d.extraction_warnings || []).length > 0 && <div className="md:col-span-2 text-amber-700"><strong>Avertissements extraction :</strong> {d.extraction_warnings.join(", ")}</div>}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
                );
              })}
              {docs.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">Aucun document.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-sm" data-testid="docs-pagination">
          <span className="text-muted-foreground" data-testid="docs-pagination-info">
            {total === 0 ? "0 document" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} sur ${total} documents`}
          </span>
          <div className="flex items-center gap-2">
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="h-8 border border-border rounded px-2 bg-background" data-testid="page-size-select">
              <option value={25}>25 / page</option><option value={50}>50 / page</option><option value={100}>100 / page</option>
            </select>
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="page-prev">Précédent</Button>
            <span data-testid="page-current">Page {page} / {Math.max(1, Math.ceil(total / pageSize))}</span>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage((p) => p + 1)} data-testid="page-next">Suivant</Button>
          </div>
        </div>
        </>
      )}
    </div>
  );
}

function ConflictsTab({ fetcher }) {
  const [slug, setSlug] = useState("");
  const [res, setRes] = useState(null);
  const run = async (e) => {
    e?.preventDefault();
    try {
      const r = await fetcher(`/admin/blue/conflicts${slug ? `?partner_slug=${encodeURIComponent(slug)}` : ""}`);
      setRes(r);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-4" data-testid="blue-conflicts-tab">
      <form onSubmit={run} className="flex gap-3 flex-wrap p-4 border border-border rounded-md bg-card">
        <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="Slug partenaire (optionnel — tous si vide)" className="flex-1" data-testid="conflicts-slug-input" />
        <Button type="submit" data-testid="conflicts-run-btn">Analyser</Button>
      </form>
      {res && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground"><strong>{res.count}</strong> conflit(s) détecté(s){res.partner_slug ? ` pour ${res.partner_slug}` : ""}.</p>
          {(res.conflicts || []).map((c, i) => (
            <div key={i} className="p-3 border border-amber-300 bg-amber-50 rounded-md text-sm" data-testid={`conflict-${i}`}>
              <div className="font-medium text-amber-900">{c.flag} — {c.partner_slug} · {c.category}</div>
              <div className="grid md:grid-cols-2 gap-3 mt-2 text-xs">
                <div><strong>A :</strong> {c.a.title} ({c.a.label})<br /><span className="text-muted-foreground">{c.a.effective_date || "?"} → {c.a.expiry_date || "?"}</span><br /><code>{c.a.id}</code></div>
                <div><strong>B :</strong> {c.b.title} ({c.b.label})<br /><span className="text-muted-foreground">{c.b.effective_date || "?"} → {c.b.expiry_date || "?"}</span><br /><code>{c.b.id}</code></div>
              </div>
            </div>
          ))}
          {res.count === 0 && <p className="text-sm text-muted-foreground">Aucun conflit — pas d'action requise.</p>}
        </div>
      )}
    </div>
  );
}

function RetrievalTab({ fetcher }) {
  const [q, setQ] = useState("");
  const [partnerSlug, setPartnerSlug] = useState("");
  const [res, setRes] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    try {
      const r = await fetcher("/admin/blue/retrieve", { method: "POST", body: JSON.stringify({ query: q, partner_slug: partnerSlug || undefined, top_k: 4 }) });
      setRes(r);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-4" data-testid="blue-retrieval-tab">
      <form onSubmit={search} className="flex gap-3 flex-wrap p-4 border border-border rounded-md bg-card">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Question de test (ex : forfaits boissons)" className="flex-1" data-testid="retrieval-query-input" />
        <Input value={partnerSlug} onChange={(e) => setPartnerSlug(e.target.value)} placeholder="Partenaire (slug, optionnel)" className="w-72" data-testid="retrieval-partner-input" />
        <Button type="submit" data-testid="retrieval-search-btn">Rechercher</Button>
      </form>
      {res && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Motif retrieval : <strong>{res.reason}</strong></p>
          {(res.chunks || []).map((c, i) => (
            <div key={i} className="p-3 border border-border rounded-md bg-card">
              <div className="text-xs text-muted-foreground mb-1">{c.partner_slug} • {c.category} • score {c.score}</div>
              <div className="text-sm whitespace-pre-wrap">{c.text}</div>
            </div>
          ))}
          {(res.chunks || []).length === 0 && <p className="text-sm text-muted-foreground">Aucun extrait autorisé pour cette requête.</p>}
        </div>
      )}
    </div>
  );
}

function GapTab({ fetcher }) {
  const [slug, setSlug] = useState("");
  const [res, setRes] = useState(null);
  const run = async (e) => {
    e.preventDefault();
    try { const r = await fetcher(`/admin/blue/gap-analysis/${encodeURIComponent(slug)}`); setRes(r); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-4" data-testid="blue-gap-tab">
      <form onSubmit={run} className="flex gap-3 flex-wrap p-4 border border-border rounded-md bg-card">
        <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="slug du partenaire" className="flex-1" data-testid="gap-slug-input" />
        <Button type="submit" data-testid="gap-run-btn">Analyser</Button>
      </form>
      {res && res.ok === false && <p className="text-sm text-destructive">{res.error}</p>}
      {res && res.ok !== false && (
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div className="p-3 border border-border rounded-md"><strong>Manquantes</strong><div>{(res.missing_categories || []).join(", ") || "—"}</div></div>
          <div className="p-3 border border-border rounded-md"><strong>Expirées</strong><div>{(res.expired_categories || []).join(", ") || "—"}</div></div>
          <div className="p-3 border border-border rounded-md"><strong>Bientôt expirées</strong><div>{(res.expiring_categories || []).join(", ") || "—"}</div></div>
          <div className="p-3 border border-border rounded-md"><strong>Date inconnue</strong><div>{(res.unknown_date_categories || []).join(", ") || "—"}</div></div>
        </div>
      )}
    </div>
  );
}

function LoreTab({ fetcher }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ kind: "nickname", title: "", content: "", visibility: "PUBLIC", triggers: "" });
  const load = useCallback(async () => {
    try { const r = await fetcher("/admin/blue/lore"); setItems(r.lore || []); }
    catch (e) { toast.error(e.message); }
  }, [fetcher]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, triggers: form.triggers.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      const r = await fetcher("/admin/blue/lore", { method: "POST", body: JSON.stringify(payload) });
      if (r.ok === false) { toast.error(r.error); return; }
      toast.success("Élément Blue Lore ajouté");
      setForm({ kind: "nickname", title: "", content: "", visibility: "PUBLIC", triggers: "" });
      load();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-6" data-testid="blue-lore-tab">
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end p-4 border border-border rounded-md bg-card">
        <div><label className="text-xs text-muted-foreground">Type</label>
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger data-testid="lore-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>{LORE_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><label className="text-xs text-muted-foreground">Visibilité</label>
          <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
            <SelectTrigger data-testid="lore-vis-select"><SelectValue /></SelectTrigger>
            <SelectContent>{LORE_VIS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><label className="text-xs text-muted-foreground">Déclencheurs (virgules)</label>
          <Input value={form.triggers} onChange={(e) => setForm({ ...form, triggers: e.target.value })} data-testid="lore-triggers-input" /></div>
        <div className="md:col-span-3"><label className="text-xs text-muted-foreground">Titre</label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lore-title-input" /></div>
        <div className="md:col-span-3"><label className="text-xs text-muted-foreground">Contenu</label>
          <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="lore-content-input" /></div>
        <Button type="submit" data-testid="lore-save-btn">Enregistrer</Button>
      </form>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((l) => (
          <div key={l.id} className="p-3 border border-border rounded-md bg-card" data-testid={`lore-item-${l.id}`}>
            <div className="text-xs text-muted-foreground">{l.kind} • {l.visibility}</div>
            <div className="font-medium mt-1">{l.title || "(sans titre)"}</div>
            <div className="text-sm whitespace-pre-wrap mt-1">{l.content}</div>
            {(l.triggers || []).length > 0 && <div className="text-xs mt-2 text-muted-foreground">Triggers : {l.triggers.join(", ")}</div>}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground">Aucun élément.</p>}
      </div>
    </div>
  );
}

function ActionsTab({ fetcher }) {
  const [reg, setReg] = useState([]);
  const [audit, setAudit] = useState([]);
  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([fetcher("/admin/blue/actions/registry"), fetcher("/admin/blue/actions/audit?limit=50")]);
      setReg(r.actions || []); setAudit(a.audit || []);
    } catch (e) { toast.error(e.message); }
  }, [fetcher]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-6" data-testid="blue-actions-tab">
      <div>
        <h3 className="text-base font-semibold mb-2">Registre d'actions</h3>
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary"><tr><th className="text-left p-2">ID</th><th className="text-left p-2">Label</th><th className="text-left p-2">Risk</th><th className="text-left p-2">Description</th></tr></thead>
            <tbody>{reg.map((a) => (
              <tr key={a.id} className="border-t border-border" data-testid={`action-def-${a.id}`}>
                <td className="p-2 font-mono text-xs">{a.id}</td><td className="p-2">{a.label}</td>
                <td className="p-2">RISK {a.risk}</td><td className="p-2 text-xs text-muted-foreground">{a.description}</td>
              </tr>))}</tbody>
          </table>
        </div>
      </div>
      <div>
        <h3 className="text-base font-semibold mb-2">Audit récent</h3>
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary"><tr><th className="text-left p-2">TS</th><th className="text-left p-2">Action</th><th className="text-left p-2">Rôle</th><th className="text-left p-2">Outcome</th></tr></thead>
            <tbody>{audit.map((e) => (
              <tr key={e.id} className="border-t border-border">
                <td className="p-2 font-mono text-xs">{e.ts}</td><td className="p-2">{e.action_id}</td>
                <td className="p-2">{e.user_role || "—"}</td><td className="p-2">{e.outcome}</td>
              </tr>))}
              {audit.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Aucune trace pour l'instant.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PasskeyPanel({ fetcher, isSuper, onChange }) {
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [friendlyName, setFriendlyName] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    if (!isSuper) { setCreds([]); setLoading(false); return; }
    try { const r = await fetcher("/admin/super-admin/webauthn/credentials"); setCreds(r.credentials || []); }
    catch (e) { setError(e.message); }
    setLoading(false);
  }, [fetcher, isSuper]);
  useEffect(() => { load(); }, [load]);

  // WebAuthn helpers (base64url <-> ArrayBuffer)
  const b64uToBuf = (s) => {
    const pad = "=".repeat((4 - (s.length % 4)) % 4);
    const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(b64);
    const buf = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
    return buf.buffer;
  };
  const bufToB64u = (buf) => {
    const bytes = new Uint8Array(buf);
    let s = "";
    for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
    return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  };

  const enroll = async () => {
    if (!isSuper) { toast.error("Rôle super_admin requis."); return; }
    if (!("credentials" in navigator) || !navigator.credentials?.create) {
      toast.error("WebAuthn indisponible dans ce navigateur."); return;
    }
    const name = (friendlyName || "").trim() || "Passkey principale";
    setEnrolling(true);
    try {
      const begin = await fetcher("/admin/super-admin/webauthn/register/begin",
        { method: "POST", body: JSON.stringify({ friendly_name: name }) });
      if (begin.ok === false) throw new Error(begin.error || "begin_failed");
      const opts = typeof begin.options === "string" ? JSON.parse(begin.options) : begin.options;
      const publicKey = {
        ...opts,
        challenge: b64uToBuf(opts.challenge),
        user: { ...opts.user, id: b64uToBuf(opts.user.id) },
        excludeCredentials: (opts.excludeCredentials || []).map((c) => ({ ...c, id: b64uToBuf(c.id) })),
      };
      const cred = await navigator.credentials.create({ publicKey });
      const attestation = {
        id: cred.id,
        rawId: bufToB64u(cred.rawId),
        type: cred.type,
        response: {
          clientDataJSON: bufToB64u(cred.response.clientDataJSON),
          attestationObject: bufToB64u(cred.response.attestationObject),
        },
        clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
      };
      const finish = await fetcher("/admin/super-admin/webauthn/register/finish",
        { method: "POST", body: JSON.stringify({ friendly_name: name, credential: attestation }) });
      if (finish.ok === false) throw new Error(finish.error || "finish_failed");
      toast.success("Passkey enregistrée");
      setFriendlyName("");
      await load();
      onChange?.();
    } catch (e) { toast.error(e.message || "Échec de l'enrôlement"); }
    setEnrolling(false);
  };

  const revoke = async (id) => {
    if (!isSuper) return;
    if (!window.confirm("Révoquer cet authentificateur ?")) return;
    try {
      const r = await fetcher(`/admin/super-admin/webauthn/revoke/${id}`, { method: "POST" });
      if (r.ok === false) throw new Error(r.error || "revoke_failed");
      toast.success("Authentificateur révoqué");
      load();
      onChange?.();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="p-4 border border-border rounded-md bg-card" data-testid="passkey-panel">
      <h3 className="text-base font-semibold mb-1">Passkeys (WebAuthn)</h3>
      <p className="text-xs text-muted-foreground mb-3">
        {isSuper
          ? "Enrôlez un authentificateur physique ou biométrique pour votre compte super_admin. Plusieurs clés sont supportées."
          : "L'enrôlement de passkey est réservé au super_admin lui-même — un admin ne peut pas gérer les facteurs d'un autre super_admin."}
      </p>
      {isSuper && (
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div className="flex-1 min-w-64">
            <label className="text-xs text-muted-foreground">Nom de l'appareil</label>
            <Input value={friendlyName} onChange={(e) => setFriendlyName(e.target.value)}
                   placeholder="MacBook / YubiKey / iPhone" data-testid="passkey-name-input" />
          </div>
          <Button onClick={enroll} disabled={enrolling} data-testid="passkey-enroll-btn">
            {enrolling ? "Enrôlement…" : "Ajouter une passkey"}
          </Button>
        </div>
      )}
      {isSuper && (
        <p className="text-xs text-muted-foreground mb-3">
          Astuce : pour une clé de secours indépendante, choisissez « Utiliser un autre appareil » ou une clé de sécurité physique (YubiKey, etc.) lorsque votre navigateur le propose. Windows Hello, Touch ID et les clés USB/NFC sont tous supportés.
        </p>
      )}
      {error && <p className="text-sm text-destructive mb-2">{error}</p>}
      {loading ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary"><tr>
              <th className="text-left p-2">Nom</th><th className="text-left p-2">Créée le</th>
              <th className="text-left p-2">Dernière utilisation</th><th className="text-left p-2">Statut</th><th className="p-2"></th>
            </tr></thead>
            <tbody>
              {creds.map((c) => (
                <tr key={c.id} className="border-t border-border" data-testid={`passkey-row-${c.id}`}>
                  <td className="p-2 font-medium">{c.friendly_name}</td>
                  <td className="p-2 font-mono text-xs">{c.created_at}</td>
                  <td className="p-2 font-mono text-xs">{c.last_used_at || "—"}</td>
                  <td className="p-2">{c.revoked ? "révoquée" : "active"}</td>
                  <td className="p-2">{isSuper && !c.revoked && (
                    <Button size="sm" variant="outline" onClick={() => revoke(c.id)} data-testid={`passkey-revoke-${c.id}`}>
                      Révoquer
                    </Button>)}</td>
                </tr>
              ))}
              {creds.length === 0 && (
                <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">
                  {isSuper ? "Aucune passkey enregistrée." : "Non applicable."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RecoveryCodesPanel({ fetcher, isSuper }) {
  const [codes, setCodes] = useState(null);
  const regen = async () => {
    if (!isSuper) return;
    if (!window.confirm("Régénérer va INVALIDER tous les codes existants. Continuer ?")) return;
    try {
      const r = await fetcher("/admin/super-admin/recovery-codes/regenerate", { method: "POST" });
      if (r.ok === false) throw new Error(r.error || "failed");
      setCodes(r.codes || []);
      toast.success("Codes régénérés — copiez-les MAINTENANT, ils ne seront plus jamais affichés.");
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="p-4 border border-border rounded-md bg-card" data-testid="recovery-panel">
      <h3 className="text-base font-semibold mb-1">Codes de secours</h3>
      <p className="text-xs text-muted-foreground mb-3">Chaque code est à usage unique. Ne les stockez qu'en lieu sûr hors-ligne.</p>
      {isSuper ? (
        <>
          <Button onClick={regen} data-testid="recovery-regen-btn">Régénérer les codes</Button>
          {codes && (
            <div className="mt-3 p-3 bg-secondary rounded font-mono text-sm" data-testid="recovery-codes-plain">
              {codes.map((c, i) => <div key={i}>{c}</div>)}
              <p className="text-xs text-muted-foreground mt-2">Ces codes ne seront plus affichés. Copiez-les maintenant.</p>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Réservé au super_admin lui-même.</p>
      )}
    </div>
  );
}

function SecurityPhonePanel({ fetcher, isSuper }) {
  const [state, setState] = useState(null);
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [reason, setReason] = useState("change");
  const [recoveryCode, setRecoveryCode] = useState("");

  const load = useCallback(async () => {
    if (!isSuper) { setState(null); return; }
    try { const r = await fetcher("/admin/super-admin/security-phone"); setState(r); }
    catch (e) { toast.error(e.message); }
  }, [fetcher, isSuper]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!phone.trim()) { toast.error("Numéro requis (format +33...)"); return; }
    setSaving(true);
    try {
      const body = { phone: phone.trim(), reason };
      if (reason === "lost_access_recovery") body.recovery_code = recoveryCode.trim();
      const r = await fetcher("/admin/super-admin/security-phone", { method: "POST", body: JSON.stringify(body) });
      if (r.ok === false) { toast.error(r.error || "Erreur"); return; }
      toast.info(r.note || "Numéro en attente");
      setPhone(""); setRecoveryCode("");
      setState(r); load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const cancel = async () => {
    if (!window.confirm("Annuler le numéro en attente ?")) return;
    try {
      const r = await fetcher("/admin/super-admin/security-phone/cancel-pending", { method: "POST" });
      toast.success("Numéro en attente annulé");
      setState(r); load();
    } catch (e) { toast.error(e.message); }
  };

  const [cooldown, setCooldown] = useState(0);
  // Tick the cooldown countdown (client-side only — server is authoritative)
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  // Rehydrate cooldown from server state so a page refresh doesn't allow bypass
  useEffect(() => {
    if (state?.cooldown_remaining_seconds && state.cooldown_remaining_seconds > 0) {
      setCooldown(state.cooldown_remaining_seconds);
    }
  }, [state?.cooldown_remaining_seconds]);

  const sendCode = async () => {
    if (sending || cooldown > 0) return;  // double-click / cooldown guard
    setSending(true);
    try {
      const r = await fetcher("/admin/super-admin/security-phone/send-otp", { method: "POST" });
      if (r.ok === false) {
        if (r.error === "rate_limited_cooldown" && r.retry_after) {
          setCooldown(r.retry_after);
          toast.warning(`Un code a déjà été envoyé — réessayez dans ${r.retry_after}s.`);
        } else if (r.error === "rate_limited") {
          toast.error("Limite horaire atteinte (3 envois/heure). Réessayez plus tard.");
        } else {
          toast.error(r.error || "Envoi impossible");
        }
      } else {
        setCooldown(r.cooldown_seconds || 60);
        toast.success(`Code envoyé au ${r.to_masked || "numéro en attente"}`);
      }
    } catch (e) { toast.error(e.message); }
    setSending(false);
  };

  const verifyCode = async () => {
    if (!/^\d{4,10}$/.test(otpCode.trim())) { toast.error("Code invalide"); return; }
    setVerifying(true);
    try {
      const r = await fetcher("/admin/super-admin/security-phone/verify-otp",
        { method: "POST", body: JSON.stringify({ otp: otpCode.trim() }) });
      if (r.ok === false) { toast.error(r.error || "Vérification échouée"); }
      else { toast.success("Numéro vérifié ✔"); setOtpCode(""); setState(r); load(); }
    } catch (e) { toast.error(e.message); }
    setVerifying(false);
  };

  return (
    <div className="p-4 border border-border rounded-md bg-card" data-testid="phone-panel">
      <h3 className="text-base font-semibold mb-1">Téléphone de validation</h3>
      <p className="text-xs text-muted-foreground mb-3">
        {isSuper
          ? "Numéro utilisé pour la validation SMS (2ᵉ facteur RISK 2). Le numéro reste privé — Blue n'a jamais accès au numéro ni aux OTP."
          : "Réservé au super_admin lui-même. Un administrateur normal ne peut ni voir ni modifier le numéro d'un super_admin."}
      </p>
      {!isSuper ? null : !state ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <>
          <div className="grid md:grid-cols-2 gap-3 text-sm mb-4">
            <div className="p-3 border border-border rounded-md" data-testid="phone-current">
              <div className="text-xs text-muted-foreground">Numéro vérifié actuel</div>
              <div className="font-medium">{state.phone_masked || "—"}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {state.phone_verified ? `Vérifié le ${state.phone_verified_at}` : "Aucun numéro vérifié"}
              </div>
            </div>
            <div className="p-3 border border-border rounded-md" data-testid="phone-pending">
              <div className="text-xs text-muted-foreground">Numéro en attente</div>
              <div className="font-medium">{state.pending_phone_masked || "—"}</div>
              {state.pending_phone_created_at && (
                <div className="text-xs text-muted-foreground mt-1">
                  En attente depuis {state.pending_phone_created_at} — motif : {state.pending_phone_reason || "change"}
                </div>
              )}
              {state.pending_phone_masked && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {state.sms_delivery_available && (
                    <Button size="sm" onClick={sendCode}
                            disabled={sending || cooldown > 0}
                            data-testid="phone-send-otp-btn">
                      {sending
                        ? "Envoi…"
                        : (cooldown > 0
                          ? `Réessayer dans ${cooldown}s`
                          : (state.verification_active ? "Renvoyer le code" : "Envoyer le code de vérification"))}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={cancel} data-testid="phone-cancel-pending">
                    Annuler
                  </Button>
                </div>
              )}
              {state.verification_active && (
                <p className="text-xs text-muted-foreground mt-2" data-testid="phone-verification-status">
                  Code envoyé — saisissez-le ci-dessous.{cooldown > 0 ? ` Nouveau renvoi possible dans ${cooldown}s.` : ""}
                </p>
              )}
            </div>
          </div>

          {state.pending_phone_masked && state.sms_delivery_available && (
            <div className="p-3 mb-3 border border-border bg-secondary/40 rounded-md flex flex-wrap gap-3 items-end" data-testid="phone-verify-box">
              <div className="flex-1 min-w-48">
                <label className="text-xs text-muted-foreground">Code reçu par SMS</label>
                <Input inputMode="numeric" pattern="[0-9]*" maxLength={10}
                       value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                       placeholder="123456" data-testid="phone-otp-input" />
              </div>
              <Button onClick={verifyCode} disabled={verifying} data-testid="phone-verify-btn">
                {verifying ? "Vérification…" : "Vérifier"}
              </Button>
            </div>
          )}

          {!state.sms_delivery_available && (
            <div className="p-3 mb-3 border border-amber-200 bg-amber-50 rounded-md text-sm">
              <strong>Vérification OTP indisponible :</strong> aucun fournisseur SMS réel actif. Le numéro peut être enregistré en attente, mais la validation par SMS interviendra uniquement lorsqu'un fournisseur réel sera opérationnel.
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground">Nouveau numéro (format international +33...)</label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33 6 12 34 56 78" data-testid="phone-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Motif</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="phone-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="change">Changement normal</SelectItem>
                  <SelectItem value="lost_access_recovery">Je n'ai plus accès à mon ancien téléphone</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {reason === "lost_access_recovery" && (
              <div className="md:col-span-2">
                <label className="text-xs text-muted-foreground">Code de secours (à usage unique) — ou utilisez une passkey de secours à l'étape de vérification</label>
                <Input value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)}
                       placeholder="xxxxxxxxxxxxx" data-testid="phone-recovery-code-input" />
                <p className="text-xs text-muted-foreground mt-1">Le code n'est PAS consommé à cette étape ; il sera vérifié lors de la validation OTP finale.</p>
              </div>
            )}
            <Button onClick={submit} disabled={saving} data-testid="phone-submit-btn">
              {saving ? "Enregistrement…" : (state.phone_configured ? "Modifier le numéro" : "Ajouter un numéro")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function SuperAdminTab({ fetcher }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [status, setStatus] = useState(null);
  const [sms, setSms] = useState(null);
  const load = useCallback(async () => {
    try {
      const [s, sm] = await Promise.all([fetcher("/admin/super-admin/status"), fetcher("/admin/super-admin/sms/status")]);
      setStatus(s); setSms(sm);
    } catch (e) { toast.error(e.message); }
  }, [fetcher]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="blue-super-admin-tab">
      <div className="p-4 border border-border rounded-md bg-card">
        <h3 className="text-base font-semibold mb-2">Super Admins ({status?.super_admin_count ?? "—"})</h3>
        <p className="text-xs text-muted-foreground mb-3">Élévation & révocation via <code>scripts/security/superadmin_manage.py</code> — jamais depuis le web.</p>
        <ul className="text-sm space-y-1">{(status?.super_admins || []).map((u) => (
          <li key={u.id} data-testid={`super-admin-${u.id}`}>
            <strong>{u.email}</strong> — {u.active_authenticators} passkey(s), {u.unused_recovery_codes} code(s) de secours
          </li>))}
          {(!status || status.super_admin_count === 0) && <li className="text-muted-foreground">Aucun super_admin enregistré.</li>}
        </ul>
      </div>
      <PasskeyPanel fetcher={fetcher} isSuper={isSuper} onChange={load} />
      <SecurityPhonePanel fetcher={fetcher} isSuper={isSuper} />
      <RecoveryCodesPanel fetcher={fetcher} isSuper={isSuper} />
      <div className="p-4 border border-border rounded-md bg-card">
        <h3 className="text-base font-semibold mb-2">Fournisseur SMS</h3>
        {sms?.human_status ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div><span className="text-muted-foreground">Mode :</span> <strong>{sms.human_status.mode}</strong></div>
            <div><span className="text-muted-foreground">Envoi réel :</span> <strong>{sms.human_status.real_send_enabled ? "activé" : "désactivé"}</strong></div>
            <div><span className="text-muted-foreground">Numéro vérifié :</span> <strong>{sms.human_status.verified_phone_present ? "Oui" : "Non"}</strong></div>
            <div className="md:col-span-2"><span className="text-muted-foreground">Action requise :</span> {sms.human_status.action_required}</div>
            <p className="md:col-span-2 text-xs text-muted-foreground mt-1">{sms.human_status.provider_activation_guard}</p>
          </div>
        ) : (
          <p className="text-sm">Chargement…</p>
        )}
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-muted-foreground">Diagnostics détaillés</summary>
          <pre className="text-xs mt-2 bg-secondary p-2 rounded overflow-auto">{JSON.stringify(sms?.real_provider_discovery, null, 2)}</pre>
        </details>
      </div>
    </div>
  );
}

export default function BlueStudioPage({ embedded = false } = {}) {
  const { user, loading } = useAuth();
  const fetcher = useAuthedFetch();
  if (loading) return <div className="p-8">Chargement…</div>;
  if (!user || !["admin", "super_admin"].includes(user.role)) return <Navigate to="/connexion" replace />;

  return (
    <div className={embedded ? "" : "container mx-auto py-8 px-4"} data-testid="blue-studio-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Blue Studio</h1>
        <p className="text-sm text-muted-foreground mt-1">Connaissances, Lore et actions — visibilité stricte par rôle. Blue n'exécute rien sans confirmation.</p>
      </div>
      <Tabs defaultValue="partners" className="w-full">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="partners" data-testid="tab-partners">Partenaires</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="conflicts" data-testid="tab-conflicts">Conflits</TabsTrigger>
          <TabsTrigger value="retrieval" data-testid="tab-retrieval">Recherche</TabsTrigger>
          <TabsTrigger value="gap" data-testid="tab-gap">Informations manquantes</TabsTrigger>
          <TabsTrigger value="lore" data-testid="tab-lore">Blue Lore</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions">Actions & audit</TabsTrigger>
          <TabsTrigger value="super" data-testid="tab-super">Super admin</TabsTrigger>
        </TabsList>
        <TabsContent value="partners" className="mt-4"><PartnersTab fetcher={fetcher} /></TabsContent>
        <TabsContent value="documents" className="mt-4"><DocumentsTab fetcher={fetcher} /></TabsContent>
        <TabsContent value="conflicts" className="mt-4"><ConflictsTab fetcher={fetcher} /></TabsContent>
        <TabsContent value="retrieval" className="mt-4"><RetrievalTab fetcher={fetcher} /></TabsContent>
        <TabsContent value="gap" className="mt-4"><GapTab fetcher={fetcher} /></TabsContent>
        <TabsContent value="lore" className="mt-4"><LoreTab fetcher={fetcher} /></TabsContent>
        <TabsContent value="actions" className="mt-4"><ActionsTab fetcher={fetcher} /></TabsContent>
        <TabsContent value="super" className="mt-4"><SuperAdminTab fetcher={fetcher} /></TabsContent>
      </Tabs>
    </div>
  );
}
